import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../libs/environment/environment';
import {
  ArBrandingUpdateRequest,
  ArCompanyInformationUpdateRequest,
  CommissionSettingsApiResponse,
  CommissionSettingsUpdateApiRequest,
  CompanySettingsAdminApiResponse,
  CompanySettingsArApiResponse,
  CompanySettingsArBranding,
  CompanySettingsArOverview,
  MyTenantApiResponse,
  MyTenantBrandingApiResponse,
  MyTenantBrandingUpdateApiRequest,
  MyTenantDocumentsApiResponse,
  MyTenantSubscriptionApiResponse,
  MyTenantUpdateApiRequest,
  TenantRetentionProfileApiResponse,
  TenantRetentionProfileUpdateApiRequest,
} from './company-settings.models';

@Injectable({ providedIn: 'root' })
export class CompanySettingsApiService {
  private readonly apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  getMyTenant(): Observable<MyTenantApiResponse> {
    return this.http.get<MyTenantApiResponse>(`${this.apiBaseUrl}/v1/business/my-tenant`);
  }

  getMyTenantBranding(): Observable<MyTenantBrandingApiResponse> {
    return this.http.get<MyTenantBrandingApiResponse>(
      `${this.apiBaseUrl}/v1/business/my-tenant/branding`,
    );
  }

  getMyTenantSubscription(): Observable<MyTenantSubscriptionApiResponse> {
    return this.http.get<MyTenantSubscriptionApiResponse>(
      `${this.apiBaseUrl}/v1/business/my-tenant/subscription`,
    );
  }

  updateMyTenant(payload: MyTenantUpdateApiRequest): Observable<MyTenantApiResponse> {
    return this.http.put<MyTenantApiResponse>(`${this.apiBaseUrl}/v1/business/my-tenant`, payload);
  }

  getMyTenantDocuments(): Observable<MyTenantDocumentsApiResponse> {
    return this.http.get<MyTenantDocumentsApiResponse>(`${this.apiBaseUrl}/v1/business/my-tenant/documents`);
  }

  uploadMyTenantDocument(file: File, displayName?: string): Observable<MyTenantDocumentsApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (displayName?.trim()) {
      formData.append('displayName', displayName.trim());
    }

    return this.http.post<MyTenantDocumentsApiResponse>(
      `${this.apiBaseUrl}/v1/business/my-tenant/documents?documentType=CompanyOther`,
      formData,
    );
  }

  replaceMyTenantDocument(fileId: string, file: File): Observable<MyTenantDocumentsApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<MyTenantDocumentsApiResponse>(
      `${this.apiBaseUrl}/v1/business/my-tenant/documents/${encodeURIComponent(fileId)}/replace`,
      formData,
    );
  }

  uploadMyTenantLogo(file: File): Observable<MyTenantBrandingApiResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<MyTenantBrandingApiResponse>(
      `${this.apiBaseUrl}/v1/business/my-tenant/branding/logo`,
      formData,
    );
  }

  getCommissionSettings(): Observable<CommissionSettingsApiResponse> {
    return this.http.get<CommissionSettingsApiResponse>(
      `${this.apiBaseUrl}/v1/business/commission-settings`,
    );
  }

  updateCommissionSettings(
    payload: CommissionSettingsUpdateApiRequest,
  ): Observable<CommissionSettingsApiResponse> {
    return this.http.put<CommissionSettingsApiResponse>(
      `${this.apiBaseUrl}/v1/business/commission-settings`,
      payload,
    );
  }

  getTenantRetentionProfile(): Observable<TenantRetentionProfileApiResponse> {
    return this.http.get<TenantRetentionProfileApiResponse>(
      `${this.apiBaseUrl}/v1/business/tenant-retention-profile`,
    );
  }

  updateTenantRetentionProfile(
    payload: TenantRetentionProfileUpdateApiRequest,
  ): Observable<TenantRetentionProfileApiResponse> {
    return this.http.put<TenantRetentionProfileApiResponse>(
      `${this.apiBaseUrl}/v1/business/tenant-retention-profile`,
      payload,
    );
  }

  updateMyTenantBranding(
    payload: MyTenantBrandingUpdateApiRequest,
  ): Observable<MyTenantBrandingApiResponse> {
    return this.http.put<MyTenantBrandingApiResponse>(
      `${this.apiBaseUrl}/v1/business/my-tenant/branding`,
      payload,
    );
  }

  // -------------------------------------------------------------------------
  // Combined Endpoint Contract: /v1/business/dashboard/company-settings
  // -------------------------------------------------------------------------

  /** Loads the entire AR company settings page in 1 call. Principal AR only. */
  getCompanySettingsAr(): Observable<CompanySettingsArApiResponse> {
    return this.http.get<CompanySettingsArApiResponse>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/ar`,
    );
  }

  /** Saves the AR Company Information tab (full replace). */
  updateArCompanyInformation(
    payload: ArCompanyInformationUpdateRequest,
  ): Observable<{ success?: boolean; isSuccess?: boolean; data?: CompanySettingsArOverview; message?: string }> {
    return this.http.put<{ success?: boolean; isSuccess?: boolean; data?: CompanySettingsArOverview; message?: string }>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/ar/company-information`,
      payload,
    );
  }

  /** Saves the AR Branding tab (colors). */
  updateArBranding(
    payload: ArBrandingUpdateRequest,
  ): Observable<{ success?: boolean; isSuccess?: boolean; data?: CompanySettingsArBranding; message?: string }> {
    return this.http.put<{ success?: boolean; isSuccess?: boolean; data?: CompanySettingsArBranding; message?: string }>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/ar/branding`,
      payload,
    );
  }

  /** Gets presigned download URL for an AR document. */
  getArDocumentDownloadUrl(
    arId: number,
    fileId: string,
  ): Observable<{ success?: boolean; isSuccess?: boolean; data?: { downloadUrl?: string; url?: string } | string; message?: string }> {
    return this.http.get<{ success?: boolean; isSuccess?: boolean; data?: { downloadUrl?: string; url?: string } | string; message?: string }>(
      `${this.apiBaseUrl}/v1/business/ars/${arId}/documents/${encodeURIComponent(fileId)}/download-url`,
    );
  }

  /** Uploads an AR document (or logo). Uses existing AR document upload endpoint. */
  uploadArDocument(
    file: File,
    documentType?: string,
  ): Observable<{ success?: boolean; isSuccess?: boolean; message?: string; data?: unknown }> {
    const formData = new FormData();
    formData.append('file', file);
    if (documentType?.trim()) {
      formData.append('documentType', documentType.trim());
    }

    return this.http.post<{ success?: boolean; isSuccess?: boolean; message?: string; data?: unknown }>(
      `${this.apiBaseUrl}/v1/business/ars/documents/upload`,
      formData,
    );
  }

  /** Loads the entire Tenant Admin company settings page in 1 call. */
  getCompanySettingsAdmin(): Observable<CompanySettingsAdminApiResponse> {
    return this.http.get<CompanySettingsAdminApiResponse>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/admin`,
    );
  }

  /** Saves the Admin Company Information tab. */
  updateAdminCompanyInformation(
    payload: MyTenantUpdateApiRequest,
  ): Observable<CompanySettingsAdminApiResponse> {
    return this.http.put<CompanySettingsAdminApiResponse>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/admin/company-information`,
      payload,
    );
  }

  /** Saves the Admin Branding tab. */
  updateAdminBranding(
    payload: MyTenantBrandingUpdateApiRequest,
  ): Observable<MyTenantBrandingApiResponse> {
    return this.http.put<MyTenantBrandingApiResponse>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/admin/branding`,
      payload,
    );
  }

  /** Saves the Admin Retention Pot tab. */
  updateAdminRetentionPot(
    payload: TenantRetentionProfileUpdateApiRequest,
  ): Observable<TenantRetentionProfileApiResponse> {
    return this.http.put<TenantRetentionProfileApiResponse>(
      `${this.apiBaseUrl}/v1/business/dashboard/company-settings/admin/retention-pot`,
      payload,
    );
  }
}
